import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDom from 'react-dom';
import throttle from 'lodash.throttle';
import cn from 'classnames';

import { SmallLoader } from 'components/Loader';
import NoScript from 'components/common/NoScript';
import { onEvent } from 'helpers/EventHelpers';
import { MartyContext } from 'utils/context';
import useMartyContext from 'hooks/useMartyContext';

import css from 'styles/components/common/imageLazyLoader.scss';

const ARBITRARY_SCREEN_HEIGHT = 768;

let lazyComponents = [];

const scrollHandler = () => {
  lazyComponents.forEach(component => {
    component.startLoadingIfVisible();
  });
};

const setupScrollHandler = () => {
  if (!ImageLazyLoader.addedScrollHandler) {
    if (window) {
      const throttleTime = 100;
      const throttledHandler = throttle(scrollHandler, throttleTime, { trailing: true });
      onEvent(window, 'scroll', throttledHandler, true); // use capture to catch any scrollable area
      onEvent(window, 'touchmove', throttledHandler, true); // use capture to catch any scrollable area
      onEvent(window, 'resize', throttledHandler);
    }
    ImageLazyLoader.addedScrollHandler = true;
  }
};

const Image = ({ src, hydraImagesByZycada, ...imgProps }) => {
  const { marketplace : { msaImagesUrlZycada, msaImagesUrl } } = useMartyContext();
  const newSrc = hydraImagesByZycada && msaImagesUrlZycada && src ? src.replace(msaImagesUrl, msaImagesUrlZycada) : src;
  return <img alt="" src={newSrc} {...imgProps} />;
};

class ImageLazyLoader extends Component {
  static addedScrollHandler = false;

  state = {};

  componentDidMount() {
    setupScrollHandler();
    lazyComponents.push(this);
    this.setState({ imageLoaded: false, jsEnabled: true, startedLoading: false });
  }

  componentDidUpdate() {
    this.startLoadingIfVisible();
  }

  componentWillUnmount() {
    lazyComponents = lazyComponents.filter(el => el !== this);
  }

  hideLoader = () => {
    this.setState({ imageLoaded: true });
  };

  startLoadingIfVisible() {
    const {
      props: { loadThreshold, shouldStartLoading, imageLoadedCallback },
      state: { jsEnabled, startedLoading }
    } = this;

    if ((!shouldStartLoading) || (!jsEnabled) || startedLoading) {
      return;
    }

    const screenHeight = typeof window === 'undefined' ? ARBITRARY_SCREEN_HEIGHT : window.innerHeight;
    const domNode = ReactDom.findDOMNode(this);

    const { left, top, width, height } = domNode.getBoundingClientRect() || {};
    const hasPosition = [left, top, width, height].some(n => n > 0);
    if (!hasPosition) {
      return;
    }

    if (top < (screenHeight * loadThreshold)) {
      this.setState({ startedLoading: true });
      if (imageLoadedCallback) {
        imageLoadedCallback();
      }
      lazyComponents = lazyComponents.filter(el => el !== this);
    }
  }

  render() {
    const {
      props: {
        imgProps,
        placeholder,
        divBgImg,
        preventOpacityTransition,
        className = '',
        pictureProps,
        forceLoad,
        hydraImagesByZycada,
        imageLoadedCallback
      },
      state: { jsEnabled, startedLoading, imageLoaded },
      hideLoader
    } = this;
    const onLoad = (...args) => {
      hideLoader(...args);
      if (imageLoadedCallback) {
        imageLoadedCallback();
      }
      if (imgProps.onLoad) {
        imgProps.onLoad(...args);
      }
    };

    const secondImg = divBgImg && <div className={cn(css.divBgImg, { [css.preventTransition]: preventOpacityTransition })}>{divBgImg}</div>;

    // Hide img if secondImg exists.
    // img is never removed because we need its onload event to fire up.
    const img = <Image {...imgProps} hydraImagesByZycada={hydraImagesByZycada} className={cn(imgProps.className, { [css.hidden]: !!secondImg, [css.preventTransition]: preventOpacityTransition })} onLoad={onLoad} />;
    // if we have started loading but haven't finished loading, show the loader
    // and the image element but make the image display:none until it has
    // finished loading.
    if (imageLoaded || forceLoad) {
      if (pictureProps.length) {// no span returned because of landing page component content box positioning;
        return (
          <picture>
            {pictureProps}
            {img}
          </picture>
        );
      }
      return <span className={cn(css.lazyLoader, css.loaded, className)}>
        {img}
        {secondImg}
      </span>;
    } else if (startedLoading) {
      return <span className={cn(css.lazyLoader, className)}>
        {img}
        {secondImg}
        {placeholder}
      </span>;
    } else if (jsEnabled) {
      return (
        <MartyContext.Consumer>
          { context => {
            const { testId } = context;
            return <span className={cn(css.lazyLoader, className)} data-test-id={testId('imageLazyLoader')}>
              {placeholder}
            </span>;
          }}
        </MartyContext.Consumer>
      );
    }
    return <span className={cn(css.lazyLoader, className)}>
      <NoScript>
        {img}
        {secondImg}
      </NoScript>
    </span>;
  }
}

ImageLazyLoader.defaultProps = {
  loadThreshold: 1,
  placeholder: <SmallLoader />,
  shouldStartLoading: true,
  pictureProps: [],
  forceLoad: false
};

ImageLazyLoader.propTypes = {
  /** these are the props passed to the nested image. you can put anything you want here. */
  imgProps: PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired
  }).isRequired,

  /** optional className to pass down to containing span */
  className: PropTypes.string,

  /** what to render while the image is loading */
  placeholder: PropTypes.object,

  /** load all images above `loadThreshold * the height of the viewport` */
  loadThreshold: PropTypes.number,

  /** remember, you can only defer the request. once this property is `true` the image will start loading. */
  shouldStartLoading: PropTypes.bool,

  /** just force render the image tag right away. essentially turns off lazyloading while maintaing the same markup. */
  forceLoad: PropTypes.bool,

  /** make an optional picture element with srcSet. needed for landing pages */
  pictureProps: PropTypes.array,

  hydraImagesByZycada: PropTypes.bool, // just a temporary hydra flag for testing a different msa image source

  imageLoadedCallback: PropTypes.func // function pointer to tell the parent component when the image has loaded.
};

export default ImageLazyLoader;
